import React from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer, Breadcrumbs } from "@/components/Navigation";
import { PRACTICE_INFO } from "@/site-data";
import {
  Calendar,
  Phone,
  ArrowRight,
  Zap,
  Activity,
  Award,
  Sparkles,
  ShieldCheck,
  CheckCircle,
  HelpCircle
} from "lucide-react";

export default function Services() {
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: "Wasatch Foot & Ankle Center - Podiatry Services",
    url: "https://www.drspenceharper.com/services/",
    telephone: PRACTICE_INFO.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: PRACTICE_INFO.address.street,
      addressLocality: PRACTICE_INFO.address.city,
      addressRegion: PRACTICE_INFO.address.state,
      postalCode: PRACTICE_INFO.address.zip,
      addressCountry: "US"
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Podiatric Treatment Services",
      itemListElement: PRACTICE_INFO.services.map((svc) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "MedicalProcedure",
          name: svc.title,
          description: svc.shortDesc
        }
      }))
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title="Podiatry Services & Treatments Heber City UT | Wasatch Foot & Ankle"
        description="Comprehensive foot and ankle care in Heber City, UT. Specializing in Swift microwave wart removal, bunion surgery, heel pain, plantar fasciitis, and neuromas."
        canonicalPath="/services/"
        schema={servicesSchema}
      />
      <Header />
      <Breadcrumbs items={[{ label: "Services" }]} />

      <main className="flex-1">
        {/* HERO TITLE */}
        <section className="bg-[#0f2b48] text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
            <span className="text-cyan-300 font-bold text-xs uppercase tracking-widest">
              Comprehensive Foot &amp; Ankle Solutions
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              Podiatric Services &amp; Advanced Treatments
            </h1>
            <p className="text-slate-200 text-base sm:text-lg max-w-2xl mx-auto font-body">
              From breakthrough non-invasive Swift microwave therapy to reconstructive foot surgery, Dr. Spence Harper provides personalized healing for every step.
            </p>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0284c7]">Care for every stage of life</p>
                <p className="mt-1 text-sm text-slate-600">New patients and second opinions are welcome.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {PRACTICE_INFO.marketingKeywords.slice(0, 8).map((keyword) => (
                  <span key={keyword} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES GRID */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {PRACTICE_INFO.services.map((svc) => (
                <div
                  key={svc.id}
                  className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                >
                  <div className="space-y-4">
                    <div className="w-12 h-12 rounded-2xl bg-cyan-50 border border-cyan-100 flex items-center justify-center text-[#0369a1]">
                      {svc.id === "swift-therapy" && <Zap className="w-6 h-6" />}
                      {svc.id === "surgical-corrections" && <Activity className="w-6 h-6" />}
                      {svc.id === "bunion-correction" && <Award className="w-6 h-6" />}
                      {svc.id === "heel-pain-plantar-fasciitis" && <Sparkles className="w-6 h-6" />}
                      {svc.id === "ingrown-toenails" && <ShieldCheck className="w-6 h-6" />}
                      {svc.id === "neuromas" && <CheckCircle className="w-6 h-6" />}
                    </div>

                    <h2 className="font-heading font-bold text-xl text-[#0f2b48]">
                      {svc.title}
                    </h2>

                    <p className="text-sm text-slate-600 leading-relaxed font-body">
                      {svc.shortDesc}
                    </p>

                    <div className="pt-3 border-t border-slate-100">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                        Key Advantages:
                      </div>
                      <ul className="text-xs text-slate-600 space-y-1.5 font-body">
                        {svc.treatments.slice(0, 2).map((t, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-700 shrink-0 mt-0.5" />
                            <span>{t}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="pt-6 mt-6 border-t border-slate-100 flex items-center justify-between">
                    <Link
                      href={`/services/${svc.slug}/`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0369a1] hover:text-[#0284c7]"
                    >
                      Read full treatment details for {svc.title} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <Link
                      href="/appointments/"
                      className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                    >
                      Request Visit
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* WHAT TO EXPECT BANNER */}
            <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-200 shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8 space-y-4">
                  <span className="text-[#0369a1] font-bold text-xs uppercase tracking-wider">
                    Our Clinical Philosophy
                  </span>
                  <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#0f2b48]">
                    Conservative First, Surgical Precision When Essential
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-body">
                    We never rush into aggressive surgeries when gentle, evidence-based conservative treatments can resolve your pain. When surgery is warranted for structural stability or deformity correction, Dr. Harper's 25+ years of board-certified surgical mastery ensures precise outcomes.
                  </p>
                </div>
                <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
                  <Link
                    href="/appointments/"
                    className="w-full text-center bg-[#0369a1] hover:bg-[#0284c7] text-white font-bold py-3.5 px-6 rounded-lg shadow transition-all"
                  >
                    Schedule Consultation
                  </Link>
                  <a
                    href={`tel:${PRACTICE_INFO.phone}`}
                    className="w-full text-center bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-3.5 px-6 rounded-lg transition-all"
                  >
                    Call: (435) 657-0329
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
